import { Routes, Route, useNavigate } from 'react-router-dom';
import { CheckoutPage } from './pages/CheckoutPage';
import { PaymentStatusPage } from './pages/PaymentStatusPage';
import { Button } from './components/ui/Button';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { useLocalStorage } from './hooks/useLocalStorage';

function HomePage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useLocalStorage<'light' | 'dark'>('theme', 'light');

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        theme === 'dark'
          ? 'bg-gray-900 text-white'
          : 'bg-gradient-to-br from-blue-50 to-indigo-100 text-gray-900'
      }`}
    >
      <div className="container mx-auto px-4 py-8">
        <div className="flex min-h-[80vh] flex-col items-center justify-center space-y-8">
          {/* Header */}
          <div className="space-y-4 text-center">
            <h1 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-4xl font-bold text-transparent md:text-6xl">
              Checkout MultiViral
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Sistema de checkout para mentoria
            </p>
          </div>

          {/* Interactive Section */}
          <div
            className={`rounded-xl p-8 shadow-md backdrop-blur-sm ${
              theme === 'dark'
                ? 'border border-gray-700 bg-gray-800/50'
                : 'border border-white/20 bg-white/70'
            }`}
          >
            <div className="space-y-6 text-center">
              <div className="space-y-4">
                <Button
                  onClick={() => navigate('/exemplo-checkout-id')}
                  size="lg"
                  className="px-8 py-4 text-lg"
                >
                  🛒 Ir para o Checkout
                </Button>

                <div className="flex justify-center gap-4">
                  <Button variant="outline" onClick={toggleTheme}>
                    {theme === 'light' ? '🌙' : '☀️'}{' '}
                    {theme === 'light' ? 'Dark' : 'Light'}
                  </Button>
                </div>
              </div>

              <div className="text-center">
                <p
                  className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  Projeto de checkout para mentoria desenvolvido com React +
                  TypeScript + Tailwind CSS
                </p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid w-full max-w-4xl grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                name: 'Checkout Responsivo',
                icon: '📱',
                desc: 'Layout adaptado para mobile e desktop',
              },
              {
                name: 'Múltiplas Formas de Pagamento',
                icon: '💳',
                desc: 'Cartão, PIX e Boleto',
              },
              {
                name: 'Validação de Dados',
                icon: '✅',
                desc: 'Formulários com validação em tempo real',
              },
              {
                name: 'Interface Moderna',
                icon: '🎨',
                desc: 'Design limpo inspirado na xgrow',
              },
              {
                name: 'TypeScript',
                icon: '🔷',
                desc: 'Tipagem estática para maior segurança',
              },
              {
                name: 'Componentes Reutilizáveis',
                icon: '🧩',
                desc: 'Arquitetura modular e escalável',
              },
            ].map(feature => (
              <div
                key={feature.name}
                className={`rounded-lg p-4 text-center transition-all hover:scale-105 ${
                  theme === 'dark'
                    ? 'border border-gray-700 bg-gray-800'
                    : 'border border-white/30 bg-white/60'
                }`}
              >
                <div className="mb-2 text-2xl">{feature.icon}</div>
                <h3 className="text-sm font-semibold">{feature.name}</h3>
                <p
                  className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary
      showDetails={import.meta.env.VITE_NODE_ENV === 'development'}
    >
      <Routes>
        <Route path="/success" element={<PaymentStatusPage />} />
        <Route path="/:id" element={<CheckoutPage />} />
        <Route path="/home" element={<HomePage />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
